import { defineComponent as _defineComponent } from 'vue'
import { computed, CreateElement, onMounted, onUnmounted, ref, watch } from 'vue';

import BasicButton from '@/ui-components/BasicButton.vue';
import ChipButton from '@/ui-components/ChipButton.vue';
import CountrySwitcherModal from '@/features/buybox/components/CountrySwitcherModal.vue';
import PlotRecommenderContentTypeFilter from './PlotRecommenderContentTypeFilter.vue';
import PlotRecommenderMonetizationTypeFilter from './PlotRecommenderMonetizationType.vue';
import PlotRecommenderPriceFilter from './PlotRecommenderPriceFilter.vue';
import PlotRecommenderProviderFilter from './PlotRecommenderProviderFilter.vue';
import PlotRecommenderRatingFilter from './PlotRecommenderRatingFilter.vue';
import RecommenderTitleCard from './RecommenderTitleCard.vue';
import Spinner from '@/ui-components/Spinner.vue';

import { ModalHelper } from '@/helpers/vfm/modal-helper';

import { getAxios } from '@/helpers/axios-helper';
import { usePlotRecommenderStore } from './usePlotRecommenderStore';

import { PopoverHelper } from '@/helpers/vfm/popover-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import {
	faAngleDown,
	faFilter,
	faArrowRight,
	faTimes,
	faChevronUp,
	faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useQuery } from '@/helpers/composables/useApollo';
import { MonetizationType } from '@/@types/graphql-types';
import {
	GetPlotRecommenderTitlesDocument,
	GetPlotRecommenderTitlesQuery,
	GetPlotRecommenderTitlesQueryVariables,
	PlotRecommenderTitleFragment,
} from './GetPlotRecommenderTitles.query';
import { MaybeElement, useElementVisibility } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'PlotRecommender',
  setup(__props) {

const languageStore = useLanguageStore();
const prStore = usePlotRecommenderStore();

const { tc } = useI18n();

// <----- State ----->
const synopsis = ref('');
const titles = ref<PlotRecommenderTitleFragment[]>([]);
const ghostwriteIndex = ref(0);
const recommendationList = ref<{ jw_entity_id: string; score: number }[]>([]);
const synopsisPresets = [
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_1'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_2'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_3'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_4'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_5'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_6'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_7'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_8'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_9'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_10'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_11'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_12'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_13'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_14'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_15'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_16'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_17'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_18'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_19'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_20'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_21'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_22'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_23'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_24'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_25'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_26'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_27'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_28'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_29'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_30'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_31'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_32'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_33'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_34'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_35'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_36'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_37'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_38'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_39'),
	tc('WEBAPP_PLOT_RECOMMENDER_SYNOPSIS_40'),
];

// the typewriter the user can interact with can be quite large and bring the recommendations below the fold depending on the viewport.
// if there was recommendation changes, we want to display that there was a change by displaying a glowing box shadow at the bottom to signal scrolling.
const isShadowScrollerShown = ref(false);
const isAdditionalFiltersShown = ref(false);
const isGhostWriting = ref(false);
const timeoutId = ref<NodeJS.Timeout | null>(null);
// <----- State ----->

const recommendationsTitleDiv = ref<MaybeElement>();
const areRecommendationsVisible = useElementVisibility(recommendationsTitleDiv);

function onScroll() {
	if (isShadowScrollerShown.value && areRecommendationsVisible.value) {
		isShadowScrollerShown.value = false;
	}
}

onMounted(() => {
	synopsisPresets.sort(() => 0.5 - Math.random());
	window.addEventListener('scroll', onScroll);
});

onUnmounted(() => window.removeEventListener('scroll', onScroll));

const matchingScores = computed(() =>
	Object.fromEntries(
		recommendationList.value.map(({ jw_entity_id, score }) => [jw_entity_id, parseInt(score * 100 + '')])
	)
);

const envAssetDir = `/${ASSETS_DIR}`;

function onChange(delay = 1500) {
	isShadowScrollerShown.value = false;

	if (timeoutId.value && synopsis.value) clearTimeout(timeoutId.value);

	if (synopsis.value) {
		timeoutId.value = setTimeout(async () => {
			isGhostWriting.value = false;

			// check if the recommendations are in viewport, otherwise display pulsing shadow box
			if (!recommendationsTitleDiv.value || !areRecommendationsVisible.value) {
				isShadowScrollerShown.value = true;
			}
		}, delay);
	}
}

async function ghostwrite() {
	const STROKE_TIME = 25;

	isShadowScrollerShown.value = false;
	isGhostWriting.value = true;

	synopsis.value = '';
	let letterIndex = 0;
	const targetSynopsis = synopsisPresets[ghostwriteIndex.value % synopsisPresets.length];

	const interval = setInterval(async () => {
		synopsis.value += targetSynopsis[letterIndex];

		if (letterIndex + 1 >= targetSynopsis.length) clearInterval(interval);

		letterIndex++;
	}, STROKE_TIME);

	const ghostWriterTimoutId = setTimeout(() => {
		onChange();
		clearTimeout(ghostWriterTimoutId);
	}, STROKE_TIME * targetSynopsis.length);

	ghostwriteIndex.value++;
}

const { onResult, loading, refetch } = useQuery<GetPlotRecommenderTitlesQuery, GetPlotRecommenderTitlesQueryVariables>(
	GetPlotRecommenderTitlesDocument,
	() => ({
		first: 10,
		country: languageStore.country,
		language: languageStore.language,
		titlesFilter: {
			packages: prStore.filters.packages,
			objectTypes: prStore.filters.objectTypes,
			price: prStore.filters.price,
			imdbScore: prStore.filters.scorings.imdb,
			tomatoMeter: prStore.filters.scorings.rottentomatoes,
			jwEntityIDs: recommendationList.value.map(item => item.jw_entity_id),
			// check if to use store filters or these defaults
			monetizationTypes: [
				MonetizationType.Free,
				MonetizationType.Ads,
				MonetizationType.Flatrate,
				MonetizationType.Buy,
				MonetizationType.Rent,
			],
		},
	}),
	() => ({ enabled: recommendationList.value.length > 0 })
);

onResult(({ data, loading }) => {
	if (loading) return;
	titles.value = (data?.popularTitles.edges ?? [])
		.map(t => t.node)
		.toSorted((a, b) => matchingScores.value[b.id] - matchingScores.value[a.id]);
});

watch(
	() => prStore.filters,
	() => refetch(),
	{ deep: true }
);

const isFetchingRecommendations = ref(false);
const isFetching = computed(() => isFetchingRecommendations.value || loading.value);

async function fetchRecommendations() {
	isFetchingRecommendations.value = true;

	const axios = getAxios();
	const response = await axios.post<{
		recommendations: { recommendation_list: { jw_entity_id: string; score: number }[] };
	}>(`${(JW_CONFIG as any).PLOT_RECOMMENDER_URL}/plot-recommender/textualrecommendations`, {
		synopsis: synopsis.value,
		output_limit: 20,
		locale: languageStore.language ?? 'en',
	});

	recommendationList.value = response.data.recommendations.recommendation_list ?? [];
	isFetchingRecommendations.value = false;

	await refetch();
}

async function openPopover(event: Event) {
	PopoverHelper.create({
		popoverProps: {
			cssClass: 'popover__filter',
			boundingElement: event.currentTarget,
		},
		component: {
			render: (h: CreateElement) => {
				const children = [
					PlotRecommenderRatingFilter,
					PlotRecommenderPriceFilter,
					PlotRecommenderMonetizationTypeFilter,
				].map((item: any) => {
					return h(item);
				});
				return h('div', children);
			},
		},
		onOpened: () => {
			isAdditionalFiltersShown.value = true;
		},
		onClosed: () => {
			isAdditionalFiltersShown.value = false;
		},
	});
}

function openCountrySwitcherModal() {
	ModalHelper.openModal(CountrySwitcherModal, { closable: true });
}

return { __sfc: true,languageStore, prStore, tc, synopsis, titles, ghostwriteIndex, recommendationList, synopsisPresets, isShadowScrollerShown, isAdditionalFiltersShown, isGhostWriting, timeoutId, recommendationsTitleDiv, areRecommendationsVisible, onScroll, matchingScores, envAssetDir, onChange, ghostwrite, onResult, loading, refetch, isFetchingRecommendations, isFetching, fetchRecommendations, openPopover, openCountrySwitcherModal, BasicButton, ChipButton, PlotRecommenderContentTypeFilter, PlotRecommenderProviderFilter, RecommenderTitleCard, Spinner, faAngleDown, faFilter, faArrowRight, faTimes, faChevronUp, faChevronDown }
}

})